/**
 * Wall-clock ↔ instant conversion for IANA time zones, built on Intl only (the
 * repo carries no date library). Used by the Advanced Timer: the operator types
 * "07:00 on Oct 12 in Pacific/Honolulu" and the overlay stores the instant.
 */

export const FALLBACK_TIME_ZONES = [
  "UTC",
  "Pacific/Honolulu",
  "America/Anchorage",
  "America/Los_Angeles",
  "America/Denver",
  "America/Phoenix",
  "America/Chicago",
  "America/New_York",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Madrid",
  "Europe/Athens",
  "Africa/Johannesburg",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Perth",
  "Australia/Sydney",
  "Pacific/Auckland",
];

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function partsFormatter(timeZone: string): Intl.DateTimeFormat {
  let formatter = formatterCache.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    formatterCache.set(timeZone, formatter);
  }
  return formatter;
}

export function isValidTimeZone(timeZone: unknown): timeZone is string {
  if (typeof timeZone !== "string" || !timeZone.trim()) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone });
    return true;
  } catch {
    return false;
  }
}

/** The browser's zone, or UTC when the runtime can't say. */
export function browserTimeZone(): string {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return isValidTimeZone(zone) ? zone : "UTC";
  } catch {
    return "UTC";
  }
}

/** Every IANA zone the runtime knows (plus UTC), falling back to a short list on older engines. */
export function availableTimeZones(): string[] {
  const supportedValuesOf = (Intl as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf;
  let zones: string[] = [];
  try {
    zones = supportedValuesOf ? supportedValuesOf("timeZone") : [];
  } catch {
    zones = [];
  }
  if (zones.length === 0) zones = FALLBACK_TIME_ZONES.slice(1);
  return zones.includes("UTC") ? zones : ["UTC", ...zones];
}

interface WallTimeParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function zonedParts(ms: number, timeZone: string): WallTimeParts {
  const values: Record<string, number> = {};
  for (const part of partsFormatter(timeZone).formatToParts(new Date(ms))) {
    if (part.type !== "literal") values[part.type] = Number(part.value);
  }
  return {
    year: values.year,
    month: values.month,
    day: values.day,
    // Some engines still emit "24" at midnight despite hourCycle h23.
    hour: values.hour === 24 ? 0 : values.hour,
    minute: values.minute,
    second: values.second,
  };
}

/** Zone offset from UTC in ms at instant `ms` (positive east of Greenwich). */
export function timeZoneOffsetMs(timeZone: string, ms: number): number {
  const parts = zonedParts(ms, timeZone);
  const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
  return asUtc - (ms - (((ms % 1000) + 1000) % 1000));
}

const WALL_TIME_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;

/**
 * "YYYY-MM-DDTHH:mm[:ss]" read as wall-clock time in `timeZone` → the instant.
 * A time inside a spring-forward gap moves forward by the gap; an ambiguous
 * fall-back time resolves to the earlier instant. Null when malformed.
 */
export function zonedWallTimeToInstant(wallTime: string, timeZone: string): Date | null {
  const match = WALL_TIME_PATTERN.exec(wallTime.trim());
  if (!match || !isValidTimeZone(timeZone)) return null;
  const [year, month, day, hour, minute, second = 0] = match.slice(1).map((value) => Number(value ?? 0));
  if (month < 1 || month > 12 || day < 1 || day > 31 || hour > 23 || minute > 59 || second > 59) return null;
  const asUtc = Date.UTC(year, month - 1, day, hour, minute, second);
  if (new Date(asUtc).getUTCDate() !== day) return null;
  // Candidate instants from the offsets either side of the wall time; the
  // earliest one that reads back as this wall time wins (fall-back overlap).
  const offsets = [timeZoneOffsetMs(timeZone, asUtc - 86_400_000), timeZoneOffsetMs(timeZone, asUtc + 86_400_000)];
  const candidates = [...new Set(offsets)].map((offset) => asUtc - offset).sort((a, b) => a - b);
  for (const candidate of candidates) {
    if (timeZoneOffsetMs(timeZone, candidate) === asUtc - candidate) return new Date(candidate);
  }
  // Spring-forward gap: no instant shows this wall time — use the pre-gap
  // offset, which lands the same distance past the gap.
  return new Date(asUtc - Math.min(...offsets));
}

/** The instant as "YYYY-MM-DDTHH:mm:ss" wall-clock time in `timeZone`. */
export function instantToZonedWallTime(date: Date, timeZone: string): string {
  const parts = zonedParts(date.getTime(), isValidTimeZone(timeZone) ? timeZone : "UTC");
  const pad = (value: number, width = 2) => String(value).padStart(width, "0");
  return `${pad(parts.year, 4)}-${pad(parts.month)}-${pad(parts.day)}T${pad(parts.hour)}:${pad(parts.minute)}:${pad(parts.second)}`;
}

/** "UTC−10:00" / "UTC+05:30" / "UTC" for the zone's offset at `at`. */
export function timeZoneOffsetLabel(timeZone: string, at: Date = new Date()): string {
  if (!isValidTimeZone(timeZone)) return "UTC";
  const offsetMinutes = Math.round(timeZoneOffsetMs(timeZone, at.getTime()) / 60_000);
  if (offsetMinutes === 0) return "UTC";
  const sign = offsetMinutes > 0 ? "+" : "−";
  const absolute = Math.abs(offsetMinutes);
  return `UTC${sign}${String(Math.floor(absolute / 60)).padStart(2, "0")}:${String(absolute % 60).padStart(2, "0")}`;
}
